import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, MapPin, ArrowUpRight } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

export const fmtSession = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: "", month: "", full: iso };
  return {
    day: d.toLocaleDateString(undefined, { day: "2-digit" }),
    month: d.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
    full: d.toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" }),
  };
};

export const useSessions = () => {
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    if (!API) return undefined;
    let alive = true;
    axios.get(`${API}/sessions`).then((r) => alive && setSessions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return sessions;
};

export default function Workshops() {
  const sessions = useSessions();
  const request = (id) => {
    window.dispatchEvent(new CustomEvent("tt-select-session", { detail: id }));
    scrollToId("enter");
  };

  return (
    <section id="workshop" data-testid="workshops-section" className="relative max-w-[1440px] mx-auto px-5 sm:px-10 py-28 sm:py-36">
      <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <div>
          <Eyebrow n="IX">The Living Workshop</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.05] max-w-3xl">
            Where the teachings become <em className="text-[#D9B47A]">felt.</em>
          </h2>
        </div>
        <p className="max-w-sm text-[#A8998C]">Breath, resonance, presence and expanded states, explored in a living container. Seats are offered by request.</p>
      </Reveal>

      {sessions.length === 0 ? (
        <Reveal className="mt-14 border border-[#D9B47A]/20 p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="workshops-empty">
          <p className="font-display text-2xl sm:text-3xl max-w-xl">The next dates are being set. Request an invitation and you will be the first to know.</p>
          <button data-testid="workshops-notify-btn" onClick={() => request(null)} className="shrink-0 rounded-full border border-[#D9B47A]/60 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">
            Request an invitation
          </button>
        </Reveal>
      ) : (
        <div className="mt-14 border-t border-[#D9B47A]/15">
          {sessions.map((s, i) => {
            const f = fmtSession(s.starts_at);
            const full = s.seats_left === 0;
            return (
              <Reveal key={s.id} y={20} delay={i * 0.06}>
                <div data-testid={`workshop-session-${s.id}`} className="group grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-6 sm:gap-10 py-8 border-b border-[#D9B47A]/15 hover:bg-[#120C0A] transition-colors duration-500 px-2">
                  <div className="text-center w-20">
                    <p className="font-display text-5xl text-[#D9B47A] leading-none">{f.day}</p>
                    <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C] mt-2">{f.month}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-2xl sm:text-3xl">{s.title}</p>
                    <p className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#CFC2B6]">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#D9B47A]" />{f.full}</span>
                      {s.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#D9B47A]" />{s.location}</span>}
                    </p>
                    {s.notes && <p className="mt-2 text-sm text-[#A8998C]">{s.notes}</p>}
                    {s.seats_left != null && (
                      <p className="mt-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#C2414F]">{full ? "Fully requested · join the waitlist" : `${s.seats_left} of ${s.seats} seats open`}</p>
                    )}
                  </div>
                  <button
                    data-testid={`workshop-request-${s.id}`}
                    onClick={() => request(s.id)}
                    className="col-span-2 sm:col-span-1 justify-self-start group/b inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors"
                  >
                    {full ? "Join waitlist" : "Request a seat"} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover/b:rotate-45" />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      )}
    </section>
  );
}
